import axios from 'axios';
import API_CONFIG from '../config/api';

// Get auth headers with token
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
    },
  };
};

// Interface for test request object
export interface TestRequest {
  _id: string;
  displayId?: number; // Display ID for tracking (شناسه)
  userId: {
    _id: string;
    name: string;
    lastName: string;
    mobile: string;
    nationalId: string;
  };
  patientInfo?: {
    firstName: string;
    lastName: string;
    nationalId: string;
    mobile: string;
    gender: string;
    patientType: string;
  };
  samplingLocation?: {
    type: 'home' | 'lab';
    needsContainer?: boolean;
    freeTransportation?: boolean;
  };
  addressInfo?: {
    region?: string;
    field?: string;
    street?: string;
    alley?: string;
    building?: string;
    unit?: string;
    number?: string;
    coordinates?: number[];
  };
  ageInfo?: {
    age: number;
  };
  insuranceInfo?: {
    type?: string;
    nationalIdImageUrl?: string;
    testListImageUrl?: string;
    prescriptionImageUrl?: string;
    insuranceBookletImageUrl?: string;
    trackingCode?: string;
    description?: string;
    basicInsurance?: string;
    supplementaryInsurance?: string;
    selectedPackage?: string;
    isComplete?: boolean;
  };
  testType: string;
  status: 'incomplete' | 'canceled' | 'reviewing' | 'approved' | 'rejected' | 'edited' | 'paid' | 'expired';
  createdAt: string;
  updatedAt: string;
  rejectionReason?: string;
  instructions?: string;
  notes?: string;
  editDeadline?: string;
  editVersion?: number;
  labResponseDateTime?: string; // date and time when lab responds
  paymentDateTime?: string; // date and time when payment is completed
  amount?: number; // Amount to be paid (in Toman)
  transportationPrice?: number; // Transportation price (هزینه ایاب و ذهاب)
  paymentExtensionRequest?: boolean; // Payment extension request
  paymentExtensionStatus?: 'pending' | 'approved' | 'rejected'; // Payment extension status
  paymentExtensionRejectionReason?: string; // Payment extension rejection reason
  samplingDate?: string; // Sampling date
  samplingTime?: string; // Sampling time
  transaction?: {
    _id: string;
    amount: number;
    gateway: string;
    refId?: string;
    paymentDate?: string;
    status: 'pending' | 'completed' | 'failed';
  };
  statusLogs?: Array<{
    timestamp: string;
    changedBy: {
      userId: string;
      userName: string;
      userType: 'admin' | 'user';
    };
    action: string;
    oldStatus?: string;
    newStatus?: string;
    details?: string;
  }>;
}

// Interface for pagination data
export interface PaginationData {
  currentPage: number;
  total: number;
  totalPages: number;
}

// Interface for paginated response
export interface PaginatedResponse {
  success: boolean;
  message?: string;
  data: TestRequest[];
  pagination: PaginationData;
}

/**
 * Get requests with optional status filter and pagination
 */
export const getRequests = async (params: {
  status?: string;
  expiredType?: 'grace' | 'complete'; // Filter for expired requests: grace = <24h, complete = 24h+
  page?: number;
  limit?: number;
  search?: string;
}) => {
  try {
    const response = await axios.get(
      API_CONFIG.getUrl('admin/requests'),
      {
        params,
        ...getAuthHeaders(),
      }
    );

    // Transform response to match the expected interface
    return {
      success: response.data.success,
      message: response.data.message,
      data: response.data.data || [],
      pagination: {
        currentPage: parseInt(response.data.page) || 1,
        total: response.data.count || 0,
        totalPages: response.data.totalPages || 1
      }
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Get a single test request by ID
 */
export const getRequestById = async (requestId: string) => {
  try {
    const response = await axios.get(
      API_CONFIG.getUrl(`test-requests/${requestId}`),
      getAuthHeaders()
    );

    return {
      success: response.data.success,
      message: response.data.message,
      data: response.data.data || null
    };
  } catch (error) {
    // If user route fails, try admin route as fallback
    try {
      const adminResponse = await axios.get(
        API_CONFIG.getUrl(`admin/requests/${requestId}`),
        getAuthHeaders()
      );

      return {
        success: adminResponse.data.success,
        message: adminResponse.data.message,
        data: adminResponse.data.data || null
      };
    } catch (adminError) {
      throw error; // Throw original error
    }
  }
};

/**
 * Update request status
 */
export const updateRequestStatus = async (
  requestId: string,
  status: string,
  rejectionReason?: string,
  instructions?: string,
  samplingLocation?: string,
  needsContainer?: boolean,
  freeTransportation?: boolean,
  amount?: number
) => {
  try {
    const payload: {
      status: string;
      rejectionReason?: string;
      instructions?: string;
      samplingLocation?: string;
      needsContainer?: boolean;
      freeTransportation?: boolean;
      amount?: number;
      labResponseDateTime?: string;
    } = { status };

    // Add lab response date time for approve/reject actions
    if (status === 'approved' || status === 'rejected') {
      payload.labResponseDateTime = new Date().toISOString();
    }

    // Add rejection reason if provided
    if (status === 'rejected' && rejectionReason) {
      payload.rejectionReason = rejectionReason;
    }

    // Add instructions if provided
    if (status === 'approved' && instructions) {
      payload.instructions = instructions;
    }

    // Add sampling location settings if provided
    if (status === 'approved' && samplingLocation) {
      payload.samplingLocation = samplingLocation;
      payload.needsContainer = needsContainer;
      payload.freeTransportation = freeTransportation;
    }

    // Add amount if provided (including zero)
    if (status === 'approved' && amount !== undefined) {
      payload.amount = amount;
    }

    const response = await axios.put(
      API_CONFIG.getUrl(`admin/requests/${requestId}/status`),
      payload,
      getAuthHeaders()
    );

    return {
      success: response.data.success,
      message: response.data.message,
      data: response.data.data || null
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Update test request amount (Admin only)
 */
export const updateRequestAmount = async (requestId: string, amount: number) => {
  try {
    const response = await axios.put(
      API_CONFIG.getUrl(`admin/requests/${requestId}/amount`),
      { amount },
      getAuthHeaders()
    );

    return {
      success: response.data.success,
      message: response.data.message,
      data: response.data.data || null
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Check if a rejected request can still be edited (edit deadline hasn't passed)
 */
export const canEditRequest = (request: TestRequest): boolean => {
  // Can only edit rejected requests with an edit deadline
  if (request.status !== 'rejected' || !request.editDeadline) {
    return false;
  }

  const deadline = new Date(request.editDeadline);
  const now = new Date();

  return now.getTime() <= deadline.getTime();
};

/**
 * Get remaining edit time in milliseconds for a rejected request
 */
export const getRemainingEditTime = (request: TestRequest): number => {
  if (request.status !== 'rejected' || !request.editDeadline) {
    return 0;
  }

  const deadline = new Date(request.editDeadline);
  const now = new Date();
  const diffMs = deadline.getTime() - now.getTime();

  return Math.max(0, diffMs);
};

/**
 * Format remaining edit time into a human-readable string
 */
export const formatRemainingEditTime = (request: TestRequest): string => {
  const remainingMs = getRemainingEditTime(request);

  if (remainingMs <= 0) {
    return 'منقضی شده';
  }

  const hours = Math.floor(remainingMs / (1000 * 60 * 60));
  const minutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 0) {
    return `${hours} ساعت و ${minutes} دقیقه`;
  } else {
    return `${minutes} دقیقه`;
  }
};

/**
 * Reset edit deadline for expired request (Admin only)
 */
export const resetEditDeadline = async (requestId: string) => {
  try {
    const response = await axios.put(
      API_CONFIG.getUrl(`admin/requests/${requestId}/reset-edit-deadline`),
      {},
      getAuthHeaders()
    );

    return {
      success: response.data.success,
      message: response.data.message,
      data: response.data.data || null
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Get dashboard statistics for admin
 */
export const getDashboardStats = async () => {
  try {
    const response = await axios.get(
      API_CONFIG.getUrl('admin/dashboard/stats'),
      getAuthHeaders()
    );

    return {
      success: response.data.success,
      data: response.data.data
    };
  } catch (error) {
    throw error;
  }
};
